import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { RoleForm } from '@/components/admin/RoleForm';
import { Skeleton } from '@/components/ui/skeleton';
import { ManageRolesContent } from '@/content/ManageRolesContent';
import { useRole, useUpdateRole } from '@/lib/hooks/useRoles';
import type {
  RoleDetailProps,
  RoleFormSubmitInput,
} from '@/lib/types/roles.types';

export function RoleEdit({ roleId }: RoleDetailProps) {
  const navigate = useNavigate();
  const { data: role, isLoading, error } = useRole(roleId);
  const updateRoleMutation = useUpdateRole();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleCancel = () => {
    navigate({
      to: '/admin/manage-roles/$roleId',
      params: { roleId },
    });
  };

  const handleSubmit = async (input: RoleFormSubmitInput) => {
    setSubmitError(null);

    try {
      const savedRole = await updateRoleMutation.mutateAsync({
        roleId,
        displayName: input.displayName,
        description: input.description,
        chipColor: input.chipColor,
      });
      navigate({
        to: '/admin/manage-roles/$roleId',
        params: { roleId: savedRole.id },
      });
    } catch (submitError) {
      setSubmitError(
        submitError instanceof Error
          ? submitError.message
          : 'Failed to save role.'
      );
    }
  };

  if (isLoading) {
    return (
      <div className="bg-slate-700">
        <div className="mx-auto w-full max-w-2xl space-y-4 px-4 pb-8 pt-3">
          <Skeleton className="h-8 w-48 rounded-full bg-slate-600" />
          <Skeleton className="h-12 w-full rounded-lg bg-slate-600" />
          <Skeleton className="h-32 w-full rounded-lg bg-slate-600" />
        </div>
      </div>
    );
  }

  if (error || !role) {
    return (
      <div className="bg-slate-700">
        <div className="mx-auto w-full max-w-2xl px-4 pb-8 pt-3">
          <div className="rounded-lg border border-red-400 bg-red-500/10 px-4 py-6 text-center text-body text-red-100">
            {error instanceof Error
              ? error.message
              : ManageRolesContent.detail.notFound}
          </div>
        </div>
      </div>
    );
  }

  return (
    <RoleForm
      role={role}
      isSubmitting={updateRoleMutation.isPending}
      submitError={submitError}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  );
}
